import ModelBaseClass from "@quasidea/oas-client-react/lib/ModelBaseClass";
import CashProcessingBatch from "../CashProcessingBatch";
import ModelProxyClass from "./ModelProxyClass";
import Person from "../Person";
import VaultAmountDetails from "../VaultAmountDetails";
import CashProcessingLineItem from "../CashProcessingLineItem";
import CollectionLocationActivity from "../CollectionLocationActivity";


/**
 * @class CashProcessingBatchBase
 * @extends ModelBaseClass
 * @property {number} id (int64)
 * @property {number} vaultId (int64)
 * @property {number} batchNumber (integer)
 * @property {Person} person
 * @property {number} startingCountedAmount
 * @property {VaultAmountDetails} startingCountedDetails
 * @property {string} startingNote
 * @property {number} startingDiscrepancyAmount
 * @property {number} endingCountedAmount
 * @property {VaultAmountDetails} endingCountedDetails
 * @property {string} endingNote
 * @property {number} endingDiscrepancyAmount
 * @property {Date} dateStarted (date and time)
 * @property {Date} dateEnded (date and time)
 * @property {[CashProcessingLineItem]} cashProcessingLineItems
 * @property {[CollectionLocationActivity]} processedCollectionLocationActivities only set on CollectionsProcessApi::batchGet() -- assuming this batch has ended, this will be all processed deposits organized by location/gamingday
 */
class CashProcessingBatchBase extends ModelBaseClass {
	/**
	 * @type {number} id (int64)
	 */
	id;
	/**
	 * @type {number} vaultId (int64)
	 */
	vaultId;
	/**
	 * @type {number} batchNumber (integer)
	 */
	batchNumber;
	/**
	 * @type {Person} person
	 */
	person;
	/**
	 * @type {number} startingCountedAmount
	 */
	startingCountedAmount;
	/**
	 * @type {VaultAmountDetails} startingCountedDetails
	 */
	startingCountedDetails;
	/**
	 * @type {string} startingNote
	 */
	startingNote;
	/**
	 * @type {number} startingDiscrepancyAmount
	 */
	startingDiscrepancyAmount;
	/**
	 * @type {number} endingCountedAmount
	 */
	endingCountedAmount;
	/**
	 * @type {VaultAmountDetails} endingCountedDetails
	 */
	endingCountedDetails;
	/**
	 * @type {string} endingNote
	 */
	endingNote;
	/**
	 * @type {number} endingDiscrepancyAmount
	 */
	endingDiscrepancyAmount;
	/**
	 * @type {Date} dateStarted (date and time)
	 */
	dateStarted;
	/**
	 * @type {Date} dateEnded (date and time)
	 */
	dateEnded;
	/**
	 * @type {[CashProcessingLineItem]} cashProcessingLineItems
	 */
	cashProcessingLineItems;
	/**
	 * only set on CollectionsProcessApi::batchGet() -- assuming this batch has ended, this will be all processed deposits organized by location/gamingday
	 * @type {[CollectionLocationActivity]} processedCollectionLocationActivities
	 */
	processedCollectionLocationActivities;

	/**
	 * Instantiates a new instance of CashProcessingBatch based on the generic object being passed in (typically from a JSON object)
	 * @param {object} genericObject
	 * @return {CashProcessingBatch}
	 */
	static create(genericObject) {
		const newCashProcessingBatch = new CashProcessingBatch();
		newCashProcessingBatch.instantiate(_modelDefinition, genericObject, ModelProxyClass.createByClassName);
		return newCashProcessingBatch;
	}

	/**
	 * Instantiates a new array of CashProcessingBatch based on the generic array being passed in (typically from a JSON array)
	 * @param {[object]} genericArray
	 * @return {[CashProcessingBatch]}
	 */
	static createArray(genericArray) {
		if (genericArray === null) {
			return null;
		}

		const newCashProcessingBatchArray = [];
		genericArray.forEach(genericObject => {
			newCashProcessingBatchArray.push(CashProcessingBatch.create(genericObject));
		});
		return newCashProcessingBatchArray;
	}
}

const _modelDefinition = [
	ModelBaseClass.createModelProperty('id', 'integer'),
	ModelBaseClass.createModelProperty('vaultId', 'integer'),
	ModelBaseClass.createModelProperty('batchNumber', 'integer'),
	ModelBaseClass.createModelProperty('person', 'Person'),
	ModelBaseClass.createModelProperty('startingCountedAmount', 'float'),
	ModelBaseClass.createModelProperty('startingCountedDetails', 'VaultAmountDetails'),
	ModelBaseClass.createModelProperty('startingNote', 'string'),
	ModelBaseClass.createModelProperty('startingDiscrepancyAmount', 'float'),
	ModelBaseClass.createModelProperty('endingCountedAmount', 'float'),
	ModelBaseClass.createModelProperty('endingCountedDetails', 'VaultAmountDetails'),
	ModelBaseClass.createModelProperty('endingNote', 'string'),
	ModelBaseClass.createModelProperty('endingDiscrepancyAmount', 'float'),
	ModelBaseClass.createModelProperty('dateStarted', 'datetime'),
	ModelBaseClass.createModelProperty('dateEnded', 'datetime'),
	ModelBaseClass.createModelProperty('cashProcessingLineItems', '[CashProcessingLineItem]'),
	ModelBaseClass.createModelProperty('processedCollectionLocationActivities', '[CollectionLocationActivity]'),
];

export default CashProcessingBatchBase;
